import {
  Dropdown,
  DropdownField,
  ETextStyleVariant,
  TDropdownFieldProps,
  Text,
  TextInput,
  useDropdownField,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  parsePhoneNumberFromString,
  PhoneNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import {
  FocusEventHandler,
  ForwardedRef,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { usePhoneDefaultCountry } from '@/hooks/usePhoneDefaultCountry';

type TPhoneNumberInputProps = {
  /** Visible label for the country and number together — the number field names itself */
  label: string;
  /**
   * Renders the label to screen readers only. Pass it only where the same words are already
   * on screen just above, as ProfileEdit's section heading is: the group keeps its name
   * either way, and that name has to be something a voice-control user can see to say.
   */
  hideLabel?: boolean;
  /** E.164 (`+40712345678`), or a legacy number stored before E.164 capture */
  value: string;
  /** Emits E.164, or an empty string once the number is cleared */
  onChange: (value: string) => void;
  /** The country on the user's account — picks the default calling code ahead of their IP */
  userCountry?: string;
  /** Name for the number input — pass the form field name so password managers and autofill can identify it */
  name?: string;
  error?: boolean;
  helpText?: string;
  disabled?: boolean;
  markAsRequired?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /** Fires from the number field only — pass react-hook-form's `field.onBlur` on `mode: 'onBlur'` forms */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  className?: string;
};

type TCountryOption = {
  code: CountryCode;
  name: string;
  callingCode: string;
};

const useCountryOptions = (locale: string): TCountryOption[] =>
  useMemo(() => {
    const regionNames = new Intl.DisplayNames([locale], { type: 'region' });
    const collator = new Intl.Collator(locale);

    return getCountries()
      .map(code => ({
        code,
        // A handful of territories have no localised name; fall back to the ISO code
        // rather than dropping them from an "exhaustive" list.
        name: regionNames.of(code) ?? code,
        callingCode: `+${getCountryCallingCode(code)}`,
      }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [locale]);

/**
 * Shows the selection as the ISO code beside its calling code — "US +1" — rather than the
 * option's label alone, which can't distinguish the 25 countries sharing +1.
 *
 * Declared at module scope on purpose: Bonfire remounts the field when its component
 * identity changes, which drops focus off the trigger every time the menu closes. What it
 * needs comes from `useDropdownField` rather than a closure, which is what allows that.
 */
const CountryDropdownField = forwardRef<HTMLButtonElement, TDropdownFieldProps>((props, ref) => {
  const { inputValue } = useDropdownField();

  // Single-select, so this is always the string form.
  const country = typeof inputValue === 'string' ? inputValue : '';

  return (
    <DropdownField
      {...props}
      // Has to be forwarded, not dropped: Dropdown anchors the popper to this element, so
      // without it the option list is never positioned and never opens.
      ref={ref}
      // `undefined` rather than an empty node while there's no country: the field takes its
      // filled styling from this, so anything renderable paints it as filled when it isn't.
      value={
        isSupportedCountry(country) ? (
          <>
            <span>{country}</span>&nbsp;
            <span className="text-gray-500">+{getCountryCallingCode(country)}</span>
          </>
        ) : undefined
      }
    />
  );
});

CountryDropdownField.displayName = 'CountryDropdownField';

const dropdownComponents = { DropdownField: CountryDropdownField };

/**
 * The significant number with its usual grouping — "7123 456789" for a GB mobile. Taken
 * from the international format rather than the national one because that leaves out the
 * trunk prefix: `formatNational` puts a leading 0 back in 106 of the 245 countries, which
 * is a digit the field never holds.
 */
const groupSignificantDigits = (parsed: PhoneNumber) =>
  parsed.formatInternational().slice(`+${parsed.countryCallingCode}`.length).trim();

/**
 * Splits a value into the country it names and its significant national number — but only
 * where that can be done by taking leading digits off, never by putting any back.
 *
 * `nationalLength` is how many digits a national number in `region` runs to, and gates the
 * one reading that can go wrong: a value has to be longer than that before its opening
 * digits may be taken for a country code, since up to that point it could still grow into a
 * number that legitimately begins with them. Dutch 3112345 is a real number as it stands,
 * so reading its 31 as the Dutch calling code would eat digits from under someone who is
 * still typing. Nothing else needs the gate — a trunk prefix or punctuation coming off
 * leaves the number itself intact at any length.
 *
 * A mobile example on purpose, not the longest number the country allows. 88 countries can
 * hold more digits than their mobile example, and every legitimate number probed inside that
 * window (32,403 of them) came through with its digits intact — while widening the bound to
 * the longest form takes the count of unprefixed calling codes we fail to recognise from 3
 * to 49. The narrower bound is the measured one.
 *
 * Returns `undefined` when the value can't be read this way, which is the caller's signal
 * to leave what's on screen alone.
 */
const decode = (text: string, region: CountryCode | undefined, nationalLength: number) => {
  const digits = text.replace(/\D/g, '');

  // A `+` or a `00` puts a country code in front outright, whatever the length. The region
  // still has to be passed alongside a `+`, which overrides it: `00` is only recognised as
  // an international prefix when libphonenumber knows whose IDD prefix it is.
  const isExplicit = text.startsWith('+') || text.startsWith('00');
  const parsed = parsePhoneNumberFromString(text, region);

  if (
    !parsed?.country ||
    !parsed.isValid() ||
    // libphonenumber rewrites as well as trims — an Argentine 011 15 2345-6789 comes back as
    // 91123456789, a digit longer than it went in, and a bare NANP local number comes back
    // with its area code restored. Anything it didn't reach by taking leading digits off is
    // left on screen as the user has it.
    !digits.endsWith(parsed.nationalNumber)
  ) {
    return undefined;
  }

  const callingCode = region ? getCountryCallingCode(region) : undefined;
  const prefixLength = digits.length - parsed.nationalNumber.length;

  // Only a calling code coming off says which country this is. A national trunk prefix is
  // removed the same way and names nothing, so the dropdown stays where the user left it.
  const tookCallingCode =
    isExplicit ||
    (!!callingCode && digits.startsWith(callingCode) && prefixLength >= callingCode.length);

  if (tookCallingCode && !isExplicit && digits.length <= nationalLength) {
    return undefined;
  }

  return {
    country: tookCallingCode ? parsed.country : region,
    // Grouped, not bare. A value only reaches here once it parses as a valid number, so
    // this is the point at which the number's own shape is known and worth showing.
    nationalNumber: groupSignificantDigits(parsed),
  };
};

/**
 * Phone number field with a country-calling-code dropdown.
 *
 * Owns the country/national split internally and emits a single E.164 string, so
 * consumers keep a plain `phone` string field. E.164 matters server-side: the API
 * validates with libphonenumber against `profile.address.country` and falls back to
 * "US" when that's empty, so a bare international number is read as American and
 * rejected. A leading `+` survives the API's cleaning regex and overrides that guess.
 *
 * The dropdown option label is the calling code, with the country name and ISO code as
 * its description; options are ordered alphabetically by country name and search matches
 * either part. The closed trigger renders neither directly — see `CountryDropdownField`.
 *
 * Deliberately does NOT emit on mount. A legacy stored number would otherwise be
 * rewritten to E.164 just by rendering the form, which counts as a phone change:
 * the API resets `verifications.phone` and ProfileEdit fires an SMS off
 * `dirtyFields.phoneNumber`. Untouched values are left exactly as they were.
 */
const PhoneNumberInputField = (
  {
    label,
    hideLabel,
    value,
    onChange,
    userCountry,
    name,
    error,
    helpText,
    disabled,
    markAsRequired,
    onFocus,
    onBlur,
    className,
  }: TPhoneNumberInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const intl = useIntl();
  const countryOptions = useCountryOptions(intl.locale);
  const defaultCountry = usePhoneDefaultCountry(userCountry);

  const groupLabelId = useId();

  const countryLabel = intl.formatMessage({
    id: '2CeSMn',
    defaultMessage: 'Country code',
    description: 'Phone number input > country calling code dropdown field label',
  });

  // Names the number field alone, which is not the same thing as the pair it sits in — the
  // visible label names that. Defaulting this to the visible label instead would have the
  // group and one of its two controls announce identically, leaving no way to tell from the
  // reading which of them has focus. It still has to contain that visible label, though, or
  // someone driving the page by voice has nothing focusable to say: the group carries the
  // words they can see, and a group can't take focus.
  const numberLabel = intl.formatMessage({
    id: 'oHhU1C',
    defaultMessage: 'Local phone number',
    description:
      'Phone number input > number field label, for screen readers. Must contain the words of the visible label above it ("Phone number"), or voice-control users have nothing focusable to say.',
  });

  const [country, setCountry] = useState<CountryCode>();
  const [nationalNumber, setNationalNumber] = useState('');
  // Values already accounted for — what we last handed to onChange, plus any stored value
  // we've since read. Either way the value echoing back doesn't get read a second time and
  // fight whatever the user is mid-way through typing.
  const emittedValue = useRef<string>();

  const selectedCountry = country ?? defaultCountry;

  const example = useMemo(
    () => (selectedCountry ? getExampleNumber(selectedCountry, examples) : undefined),
    [selectedCountry],
  );

  // How many digits a national number in this country runs to — what decides whether a
  // value is long enough for its opening digits to be a country code.
  const nationalLength = example?.nationalNumber.length ?? 0;

  // Shape of a real number in the selected country, digits masked — "XXX XXX XXXX" for US,
  // "XXXX XXXXXX" for GB. Masking matters: an unmasked example reads as a number already in
  // the field. Built the same way the field's own contents are, so the hint promises exactly
  // the digits the field will keep rather than a trunk prefix it strips back off.
  const placeholder = example && groupSignificantDigits(example).replace(/\d/g, 'X');

  useEffect(() => {
    if (value === emittedValue.current) {
      return;
    }

    // Read through the same rules a typed or pasted number goes through, so a stored value
    // and the same number entered by hand end up looking alike. A legacy value carries no
    // `+`, so the selected country has to be offered as the region or there is nothing to
    // read it against.
    const decoded = value ? decode(value.trim(), selectedCountry, nationalLength) : undefined;

    if (decoded) {
      // Marked as seen, so a country arriving later doesn't split an already-split value
      // a second time.
      emittedValue.current = value;
      setCountry(decoded.country);
      setNationalNumber(decoded.nationalNumber);
      return;
    }

    // Deliberately not marked as seen. The IP lookup can land after mount, and a legacy
    // number has nothing but the region to be read against, so a country arriving later
    // gets another go at it. Until then it shows as it was stored rather than being guessed.
    setNationalNumber(value ?? '');
  }, [value, selectedCountry, nationalLength]);

  const emit = (nextCountry: CountryCode | undefined, nextNationalNumber: string) => {
    const digits = nextNationalNumber.replace(/\D/g, '');

    // A value still carrying its `+` names its own country code already — including the
    // non-geographic ranges (+800 freephone, +870 satellite), which belong to no country, so
    // never move the dropdown and only ever arrive here. Reading those digits under the
    // selected country would prefix a second calling code onto a number that already had one.
    const isPrefixed = nextNationalNumber.trim().startsWith('+');

    // Parsing (rather than joining) drops national trunk prefixes — Romanian
    // 0712 345 678 has to become +40712345678, not +400712345678. While the number is
    // still too short to parse, fall back to a join so validation sees the whole value.
    // With no country there's no calling code to join, so the digits go out bare — which
    // is what these fields submitted before E.164 capture, and the validator reads them
    // under the same US fallback the API does. That splits on digit shape, not country:
    // anything that fits a NANP number passes (a GB mobile typed without its trunk 0
    // does), everything else is rejected. Still better than joining a calling code the
    // user never picked, which would submit a number that's wrong rather than unprefixed.
    const parsed = digits
      ? parsePhoneNumberFromString(isPrefixed ? `+${digits}` : digits, nextCountry)
      : undefined;

    // The same thing `decode` refuses to do on screen, refused on the wire. Across the 20
    // NANP territories a bare local number comes back with its area code restored — 2642351
    // in Anguilla parses as (264) 264-2351 — which is a valid number, so nothing downstream
    // would question four digits the user never typed. Falling through to the join instead
    // submits what they did type, which fails validation and tells them. Length rather than
    // a suffix test, because libphonenumber also reorders without adding: an Argentine
    // 011 15 2345-6789 legitimately comes back as 91123456789.
    const reduced =
      parsed && parsed.nationalNumber.length <= digits.length ? parsed.number : undefined;
    const joined = nextCountry ? `+${getCountryCallingCode(nextCountry)}${digits}` : digits;
    const next = digits ? (reduced ?? (isPrefixed ? `+${digits}` : joined)) : '';

    emittedValue.current = next;
    onChange(next);
  };

  const handleCountryChange = (nextCountryCode: string) => {
    const nextCountry = nextCountryCode as CountryCode;

    setCountry(nextCountry);

    // Picking a country for an empty field changes nothing worth submitting, and staying
    // quiet keeps the form clean — on ProfileEdit an emit here would mark the phone dirty
    // and cost the user an SMS plus their phone verification on save.
    if (!nationalNumber.replace(/\D/g, '')) {
      return;
    }

    // What's on screen stays exactly where it is. It was already cut back to its significant
    // digits when it was read, and re-reading it here would be reading it under the country
    // being left — which costs a leading digit in the countries whose national numbers really
    // do open with one, so switching away and back would not return the number itself. The
    // visible cost is that the grouping stays the one it was read under: 415 555 0132 keeps
    // its US spacing after a switch to Benin. Its digits are what get submitted, and they
    // are the ones the user typed.
    emit(nextCountry, nationalNumber);
  };

  const handleNationalNumberChange = (input: string) => {
    const trimmed = input.trim();
    const decoded = decode(trimmed, selectedCountry, nationalLength);

    // Show the significant number and let the dropdown carry the calling code. Punctuation
    // goes with it: what comes back is the number itself, not the keystrokes it arrived as.
    if (decoded) {
      setCountry(decoded.country);
      setNationalNumber(decoded.nationalNumber);
      emit(decoded.country, decoded.nationalNumber);
      return;
    }

    // Drop anything that can't be part of a national number, so from the first keystroke
    // on, what's on screen and what gets submitted stay the same digits. Two exceptions,
    // both of them a reader declining to rewrite the field: a legacy stored value is read
    // for display but never rewritten, which is what keeps a render from counting as a
    // phone change; and where libphonenumber reorders rather than trims — an Argentine
    // 011 15 2345-6789 — the screen keeps the typed digits while the wire carries the
    // canonical +5491123456789. The leading `+` has to survive: it's what
    // lets the branch above recognise a typed international number once enough digits
    // arrive, and stripping it would silently re-read those digits as a national number
    // under the default country.
    const nextNationalNumber =
      (trimmed.startsWith('+') ? '+' : '') + input.replace(/[^\d\s()\-.]/g, '');

    // Pin whichever country the user was looking at as they typed. The IP lookup can
    // still be in flight, and letting it land afterwards would move the calling code
    // out from under a number that was already emitted against the old one.
    setCountry(selectedCountry);
    setNationalNumber(nextNationalNumber);
    emit(selectedCountry, nextNationalNumber);
  };

  return (
    // A group rather than a bare div: the country and the number are two controls making up
    // one answer, and this is what ties the visible label to both of them. Without it that
    // label is announced in reading order and then nowhere near the controls it belongs to.
    <div role="group" aria-labelledby={groupLabelId} className={cn('w-full', className)}>
      <Text
        id={groupLabelId}
        variant={ETextStyleVariant.SmallRegular}
        className={cn(hideLabel ? 'sr-only' : 'mb-1')}>
        {label}
        {/* Hidden from the accessible name: this text now labels the group, so an unhidden
            asterisk gets read out as "star" before either control. `aria-required` on the
            input below already carries the fact. */}
        {markAsRequired && (
          <span aria-hidden="true" className="text-red-600">
            {' *'}
          </span>
        )}
      </Text>

      <div className="flex items-start gap-1">
        <Dropdown
          // No visible `label` — the field above names the pair. Bonfire then renders
          // `ariaLabel` into a visually hidden span, so the trigger still announces as
          // "Country code US +1". This has to differ from the address field's "Country" on
          // the profile edit page, or the two triggers are indistinguishable to screen
          // readers and to anything querying by name. No required signal here on purpose:
          // a country is always selected, so there is nothing for the user to supply.
          ariaLabel={countryLabel}
          components={dropdownComponents}
          // Empty string is Bonfire's "nothing selected" — `CountryDropdownField` reads it
          // back and paints the trigger unfilled.
          inputValue={selectedCountry ?? ''}
          onChange={handleCountryChange}
          searchEnabled
          searchPlaceholder={intl.formatMessage({
            id: 'BZKgci',
            defaultMessage: 'Search countries',
            description: 'Phone number input > country calling code dropdown search placeholder',
          })}
          matchDropdownWidth={false}
          disabled={disabled}
          error={error}
          // Bonfire portals both of these to document.body (react-aria <Overlay>), so they
          // stack against modal roots as siblings rather than nesting inside them. The
          // defaults — 1400 for the desktop popper, 1310 for the mobile sheet — lose to the
          // auth modals, which set `!z-[1401]` (SignUpModal, LoginModal), leaving the option
          // list invisible behind the modal.
          overlayContainerClassName="!z-[1402]"
          sheetClassName="!z-[1402]"
          className="w-36 shrink-0">
          {countryOptions.map(option => (
            <Dropdown.Option
              key={option.code}
              value={option.code}
              content={{
                label: option.callingCode,
                description: `${option.name} (${option.code})`,
              }}
            />
          ))}
        </Dropdown>

        <TextInput
          // The number field rather than the group, so react-hook-form can focus it on a failed submit.
          ref={ref}
          controlled
          className="grow"
          name={name}
          // `tel` rather than `tel-national`, even though this input only holds the national
          // part: browsers fill it with the full stored number, and when that arrives with a
          // `+` the handler below recovers the country from it. Asking for `tel-national`
          // would drop that signal and leave the dropdown on its default.
          autoComplete="tel"
          // Both, and both the same: `onlyValue` drops the floating label from view, so this
          // one is never read by eye, and letting it stay the group's would leave the input's
          // own name behind an override no call site should have to remember to pass.
          label={numberLabel}
          ariaLabel={numberLabel}
          placeholder={placeholder}
          inputValue={nationalNumber}
          onChange={handleNationalNumberChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          markAsRequired={markAsRequired}
          error={error}
          helpText={helpText}
          onlyValue
          // Bonfire's `error` prop styles the field but sets no `aria-invalid` on the input
          // (only DropdownField and FileUploader do), so both have to be passed explicitly
          // to match the other fields on these forms.
          otherAriaProps={{
            'aria-required': markAsRequired,
            'aria-invalid': error,
            'aria-errormessage': helpText,
          }}
        />
      </div>
    </div>
  );
};

export const PhoneNumberInput = forwardRef(PhoneNumberInputField);
